import type { PortalKey } from "../../i18n/portal.js";
import { portalLocale, portalText } from "../../i18n/portal.js";
/**
 * The date and money shapes Billing repeats, in one place.
 *
 * Nothing here formats money — `src/money.ts` owns that, and it takes a `Money` so the
 * currency travels with the amount. What lives here is the month arithmetic the screen needs
 * and the two derived figures the design puts on the "This month" card.
 */
import {
  CONFIRMATION_CODE_LEDGER_ENTRY_KIND,
  CURRENCY_CODE,
  LEDGER_ENTRY_KIND,
  SUBSCRIPTION_FEE_LEDGER_ENTRY_KIND,
  money,
  type LedgerEntryKind,
  type Money,
  type CreditFigures,
  type ConfirmationCodeLedgerEntryKind,
  type CreditPartLine,
  type SubscriptionFeeLedgerEntryKind,
} from "@wetalk/domain";

import { talkTimeText } from "../../talk-time.js";

/** `YYYY-MM-DD` in the browser's own zone, which is the zone the customer is reading in. */
export function dayKeyOf(at: Date): string {
  const year = String(at.getFullYear());
  const month = String(at.getMonth() + 1).padStart(2, "0");
  const day = String(at.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
}

export function firstOfMonth(at: Date): Date {
  return new Date(at.getFullYear(), at.getMonth(), 1);
}

/** `September 2026`. The month the screen is reporting on. */
export function monthLabel(at: Date): string {
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { month: "long", year: "numeric" }).format(at);
}

/** `1 October`. When this month's invoice is issued — the first of the next one. */
export function billedOnLabel(at: Date): string {
  const next = new Date(at.getFullYear(), at.getMonth() + 1, 1);
  return new Intl.DateTimeFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { day: "numeric", month: "long" }).format(next);
}

const SHORT_MONTH = [
  "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
] as const;

/**
 * `1 Sep` / `19 Sep` — the two ends of the minutes summary's axis. Three-letter months, as the
 * design writes them; `Intl`'s `en-IE` short month is `Sept`.
 */
export function shortDayLabel(dayKey: string): string {
  if (portalLocale() === "el") return new Intl.DateTimeFormat("el-GR", { day: "numeric", month: "short", timeZone: "UTC" }).format(new Date(`${dayKey}T12:00:00Z`));
  const [year, month, day] = dayKey.split("-");
  if (year === undefined || month === undefined || day === undefined) return dayKey;
  const name = SHORT_MONTH[Number(month) - 1];
  if (name === undefined) return dayKey;
  return `${String(Number(day))} ${name}`;
}

/**
 * What the month is on course to cost, from what it has cost so far.
 *
 * A straight-line projection over elapsed days, and it is labelled "about" on the screen
 * because that is what it is. It is derived from measured spend rather than from a rate card,
 * so a month with no usage projects nothing rather than projecting a fiction.
 */
export function projectedMonthSpend(spentSoFar: Money, at: Date): Money {
  const dayOfMonth = at.getDate();
  const daysInMonth = new Date(at.getFullYear(), at.getMonth() + 1, 0).getDate();
  if (dayOfMonth <= 0 || spentSoFar.amountMinor === 0) return spentSoFar;
  return money(
    Math.round((spentSoFar.amountMinor / dayOfMonth) * daysInMonth),
    spentSoFar.currency,
  );
}

/** Zero, in the platform's currency — for a sum that has nothing to sum. */
export function noMoney(): Money {
  return money(0, CURRENCY_CODE);
}

/** Adds money that shares a currency. Raises rather than silently mixing two currencies. */
export function sumMoney(values: readonly Money[]): Money {
  if (values.length === 0) return noMoney();
  const first = values[0];
  if (first === undefined) return noMoney();
  let total = 0;
  for (const one of values) {
    if (one.currency !== first.currency) {
      throw new Error(
        portalText("finance", "m0138", { value1: one.currency, value2: first.currency }),
      );
    }
    total += one.amountMinor;
  }
  return money(total, first.currency);
}

/** `3,118` — counts on this screen are grouped the way the design groups them. */
export function groupedCount(value: number): string {
  return new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), { maximumFractionDigits: 0 }).format(value);
}

/**
 * `+€500.00` / `−€206.00`. The sign is the design's: a real minus sign, and a plus on money
 * that came in, so a column of movements reads without a legend.
 */
export function formatSignedMoney(amount: Money): string {
  const whole = new Intl.NumberFormat((portalLocale() === "el" ? "el-GR" : "en-IE"), {
    style: "currency",
    currency: amount.currency,
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Math.abs(amount.amountMinor) / 100);
  if (amount.amountMinor > 0) return `+${whole}`;
  if (amount.amountMinor < 0) return `−${whole}`;
  return whole;
}

/** `visa` → `Visa`: the processor's brand code, as a person writes it. */
export function cardBrandLabel(brand: string): string {
  if (brand === "mastercard") return portalText("finance", "m0139");
  if (brand === "amex") return portalText("finance", "m0140");
  return brand.length === 0 ? brand : `${brand.charAt(0).toUpperCase()}${brand.slice(1)}`;
}

/** `04 / 29` — the expiry exactly as it is printed on the card. */
export function cardExpiryLabel(expiryMonth: number, expiryYear: number): string {
  return `${String(expiryMonth).padStart(2, "0")} / ${String(expiryYear % 100).padStart(2, "0")}`;
}

/**
 * The `ledger_entry.kind` a row carries, or `null` for one this console does not know.
 *
 * The API sends the column verbatim — migration 0001's CHECK, `LEDGER_ENTRY_KIND` in the domain:
 * `talk_charge`, `credit_purchase`, `refund`, `adjustment`, `review_charge`, `trial_grant`,
 * `number_charge`. These are the only names the money rows are read by; a console that matched
 * on names the API never sends (`top_up`, `usage` — the pre-2026-09-23 reading) draws every real
 * row as an unlabelled receipt paid by "Credit".
 */
export function ledgerKindOf(kind: string): LedgerEntryKind | null {
  const known: readonly string[] = LEDGER_ENTRY_KIND;
  return known.includes(kind) ? (kind as LedgerEntryKind) : null;
}

/**
 * plan-020 WAPP-BILL (FEE hand-off E; design §32.8.1): the two legs of a paid plan fee — a net-zero pair on the same
 * balance, `subscription_payment` (the card payment in) and `subscription_charge` (the fee out) — or `null` for any
 * other row. Kept apart from {@link ledgerKindOf}, whose kinds are the console's labelled list (the domain keeps the
 * fee's kinds apart from `LEDGER_ENTRY_KIND` the same way).
 */
export type PlanFeeLedgerKind = SubscriptionFeeLedgerEntryKind | "subscription_charge";

export function planFeeLedgerKindOf(kind: string): PlanFeeLedgerKind | null {
  if (kind === "subscription_charge") return kind;
  const fee: readonly string[] = SUBSCRIPTION_FEE_LEDGER_ENTRY_KIND;
  return fee.includes(kind) ? (kind as SubscriptionFeeLedgerEntryKind) : null;
}

/**
 * plan-029 STMT-CODE (design §40.25.1; ADR 0082, proposed; the owner's AP-0 answer D-9): one confirmation code sent,
 * charged to the balance at the price pinned at the send — `confirmation_code_charge`, or `null` for any other row. Kept
 * apart from {@link ledgerKindOf}, whose kinds are the console's labelled list, as the domain keeps the kind apart from
 * `LEDGER_ENTRY_KIND`. The row's own description is the ledger writer's English ("Confirmation code"), so the label is
 * always the catalog's, in the page language.
 */
export function confirmationCodeLedgerKindOf(kind: string): ConfirmationCodeLedgerEntryKind | null {
  const code: readonly string[] = CONFIRMATION_CODE_LEDGER_ENTRY_KIND;
  return code.includes(kind) ? (kind as ConfirmationCodeLedgerEntryKind) : null;
}

interface LedgerRowText {
  readonly kind: string;
  readonly description: string;
  readonly billedSeconds: number | null;
  readonly amount: Money;
}

/**
 * The description a card top-up is written with when auto-reload made it (`packages/db`'s
 * `creditDescriptionOf`: a merchant-initiated charge). A manual purchase is "Credit purchase".
 */
const AUTO_RELOAD_DESCRIPTION = "Automatic top-up";

/** The design's separator between a row's label and what qualifies it (`Refund · failed test calls`). */
const QUALIFIER = " · ";

/** `Refund` + `to card` → `Refund to card`; `Refund` + `failed test calls` → `Refund · failed test calls`. */
function qualified(label: string, description: string): string {
  if (description.length === 0 || description === label) return label;
  if (description.startsWith(label)) return description;
  return `${label}${QUALIFIER}${description}`;
}

/** Display only ledger wording emitted by our system; preserve customer/operator notes verbatim. */
export function systemLedgerDescriptionText(description: string): string {
  const exact: Readonly<Record<string, PortalKey<"finance">>> = {
    "Automatic top-up": "ledger.system.topUp", "Credit purchase": "ledger.system.purchase",
    "Refund to card": "ledger.system.refund", "30 free minutes": "ledger.system.trial",
    "Card dispute decided for wetalk": "ledger.system.dispute",
    "Credit from wetalk": "ledger.system.supportCredit",
    "Credit from wetalk support": "ledger.system.supportCreditDetail",
    "Refund · failed test calls": "ledger.system.failedTests",
  };
  const key = exact[description];
  if (key !== undefined) return portalText("finance", key);
  for (const [prefix, key] of [["Credit moved to ", "ledger.system.movedTo"], ["Credit moved from ", "ledger.system.movedFrom"]] as const) {
    if (description.startsWith(prefix)) return portalText("finance", key, { counterpart: description.slice(prefix.length) });
  }
  const invoice = /^Card payment received for an invoice already settled \((\d{4}-\d{2})\) — kept as credit$/.exec(description);
  if (invoice?.[1] !== undefined) return portalText("finance", "ledger.system.settledInvoice", { month: invoice[1] });
  return description;
}

/** Refund policy supplied by the API, translated only for the known system rule. */
export function refundRuleText(rule: string): string {
  return rule === "Only unused purchased credit is refundable. Credit from wetalk, promotions and free trial time are used first and never refunded."
    ? portalText("finance", "refund.rule") : rule;
}

/** Whether a card top-up was the auto-reload's, by the wording the ledger writer gives it. */
export function isAutoReload(description: string): boolean {
  return description.startsWith(AUTO_RELOAD_DESCRIPTION);
}

/**
 * The day close's note on a talk charge (`packages/db` `usage-daily.ts` `descriptionOf`, written in English): "Talk time
 * <day> — N seconds (M included in your plan, K covered by free minutes)". plan-020 AP2-FIX: the seconds the plan's
 * included minutes (and free minutes) paid for are said in the page language after the label, through the catalog —
 * "Call minutes · 16 min 40 s (10 min 00 s included in your plan)". A note the portal does not know is left out, never
 * shown in English on a Greek page.
 */
const TALK_NOTE = /\(([^()]*)\)$/;
const TALK_NOTE_PART: readonly (readonly [RegExp, PortalKey<"finance">])[] = [
  [/^(\d+) included in your plan$/, "ledger.talk.included"],
  [/^(\d+) covered by free minutes$/, "ledger.talk.covered"],
];

function talkNoted(label: string, description: string): string {
  const note = TALK_NOTE.exec(description)?.[1];
  if (note === undefined) return label;
  const parts: string[] = [];
  for (const piece of note.split(", ")) {
    const known = TALK_NOTE_PART.map(([pattern, key]) => [pattern.exec(piece)?.[1], key] as const).find(([second]) => second !== undefined);
    if (known?.[0] === undefined) return label;
    parts.push(portalText("finance", known[1], { time: talkTimeText(Number(known[0])) }));
  }
  return portalText("finance", "ledger.talk.withNote", { label, note: parts.join(", ") });
}

/**
 * The part of a money row the design calls its label (`Auto-reload`, `Call minutes · 412 min 08 s`,
 * `Call review by WeTalk`, `Refund · failed test calls`), from the row's KIND — the prototype's
 * wording wherever the Transactions screen has a row of that kind (WeTalk.dc.html, `txn.all`).
 */
export function ledgerLabelOf(row: LedgerRowText): string {
  // A plan fee's two legs: the label alone, in the page language — the rows' own description is written in English
  // by the ledger writer (`subscriptionFeeLineTextOf`), so it is not appended to a Greek label.
  const fee = planFeeLedgerKindOf(row.kind);
  if (fee !== null) return portalText("billing", fee === "subscription_payment" ? "ledger.subscription_payment" : "ledger.subscription_charge");
  // plan-029 STMT-CODE: "Confirmation code", never the writer's English description on a Greek page.
  if (confirmationCodeLedgerKindOf(row.kind) !== null) return portalText("billing", "ledger.confirmation_code_charge");
  const kind = ledgerKindOf(row.kind);
  switch (kind) {
    case "talk_charge":
      return row.billedSeconds === null
        ? row.description
        : talkNoted(portalText("finance", "m0141", { value1: QUALIFIER, value2: talkTimeText(row.billedSeconds) }), row.description);
    case "credit_purchase":
      return isAutoReload(row.description) ? portalText("finance", "m0142") : portalText("finance", "m0143");
    case "review_charge":
      return portalText("finance", "m0144");
    case "refund":
      return qualified(portalText("finance", "m0032"), systemLedgerDescriptionText(row.description));
    case "adjustment":
      // An operator's goodwill credit (or a correction) — "Adjustment · Credit from WeTalk support".
      return qualified(portalText("finance", "m0145"), systemLedgerDescriptionText(row.description));
    case "number_charge":
      return qualified(portalText("finance", "m0146"), systemLedgerDescriptionText(row.description));
    case "trial_grant":
      // "30 free minutes": the grant's own words are the label.
      return systemLedgerDescriptionText(row.description);
    case null:
      return systemLedgerDescriptionText(row.description);
  }
}

/**
 * How a money row was paid, the design's Method column: a card purchase names the card when the
 * row says which (the part after `·`), else `Card`; a refund that went back to the card says
 * `Card`; a free-minutes grant says `Free minutes`; everything drawn from or added to the balance
 * says `Credit`.
 */
export function ledgerMethodOf(row: LedgerRowText): string {
  // Both legs of a plan fee were paid by card: the payment leg brought the card's money in, the fee leg spent it.
  if (planFeeLedgerKindOf(row.kind) !== null) return portalText("finance", "m0147");
  // plan-029 STMT-CODE: a confirmation code is drawn from the balance, like talk time.
  if (confirmationCodeLedgerKindOf(row.kind) !== null) return portalText("finance", "m0148");
  const kind = ledgerKindOf(row.kind);
  switch (kind) {
    case "credit_purchase": {
      const cut = row.description.indexOf(QUALIFIER);
      return cut < 0 ? portalText("finance", "m0147") : row.description.slice(cut + QUALIFIER.length);
    }
    case "refund":
      // Negative: the money left the balance for the card. Positive: it came back to the balance.
      return row.amount.amountMinor < 0 ? portalText("finance", "m0147") : portalText("finance", "m0148");
    case "trial_grant":
      return portalText("finance", "m0149");
    case "talk_charge":
    case "review_charge":
    case "adjustment":
    case "number_charge":
      return portalText("finance", "m0148");
    case null:
      return "—";
  }
}

export type UsageRange = "7 days" | "This month" | "Last month" | "90 days";

/** The first and last day a usage range covers, both inclusive, in the reader's own zone. */
export function usageRangeOf(
  range: UsageRange,
  at: Date,
): { readonly fromDay: string; readonly toDay: string } {
  if (range === "7 days") {
    return {
      fromDay: dayKeyOf(new Date(at.getFullYear(), at.getMonth(), at.getDate() - 6)),
      toDay: dayKeyOf(at),
    };
  }
  if (range === "Last month") {
    return {
      fromDay: dayKeyOf(new Date(at.getFullYear(), at.getMonth() - 1, 1)),
      toDay: dayKeyOf(new Date(at.getFullYear(), at.getMonth(), 0)),
    };
  }
  if (range === "90 days") {
    return {
      fromDay: dayKeyOf(new Date(at.getFullYear(), at.getMonth(), at.getDate() - 89)),
      toDay: dayKeyOf(at),
    };
  }
  return { fromDay: dayKeyOf(firstOfMonth(at)), toDay: dayKeyOf(at) };
}

export function usageRangeLabel(range: UsageRange): string {
  const labels: Readonly<Record<UsageRange, string>> = {
    "7 days": portalText("finance", "m1197"),
    "This month": portalText("finance", "m0182"),
    "Last month": portalText("finance", "m1198"),
    "90 days": portalText("finance", "m1199"),
  };
  return labels[range];
}

/** Domain credit arithmetic is unchanged; display copy and money follow the selected portal language. */
export function portalAvailableTextOf(figures: CreditFigures): string {
  return new Intl.NumberFormat(portalLocale() === "el" ? "el-GR" : "en-IE", { style: "currency", currency: figures.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(figures.availableMinor / 100);
}

export function portalCreditPartsOf(figures: CreditFigures): readonly CreditPartLine[] {
  const amount = (minor: number) => new Intl.NumberFormat(portalLocale() === "el" ? "el-GR" : "en-IE", { style: "currency", currency: figures.currency, minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(minor / 100);
  const lines: CreditPartLine[] = [
    { part: "balance", label: portalText("finance", "credit.label.balance"), text: amount(figures.balanceMinor), note: null },
    { part: "unbilled_today", label: portalText("finance", "credit.label.unbilled_today"), text: `− ${amount(figures.unbilledTodayMinor)}`, note: null },
    { part: "held", label: portalText("finance", "credit.label.held"), text: `− ${amount(figures.reservedMinor)}`, note: figures.heldConversationCount <= 0 ? null : portalText("finance", "credit.held", { count: figures.heldConversationCount }) },
  ];
  if (figures.hasTrialGrant || figures.trialSecondRemaining > 0) lines.push({ part: "trial", label: portalText("finance", "credit.label.trial"), text: portalText("finance", "credit.trial", { count: Math.floor(figures.trialSecondRemaining / 60) }), note: portalText("finance", "credit.trialNote") });
  return lines;
}
